// Location Service for GPS and Pincode-based location detection

export class LocationService {
  
  // Get user's current GPS location
  static getCurrentLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('GPS not supported by this browser. Please enter pincode manually.'));
        return;
      }

      const options = {
        enableHighAccuracy: false, // Changed to false for better compatibility
        timeout: 15000, // Increased timeout
        maximumAge: 300000 // 5 minutes cache
      };

      // Add user interaction check
      if (document.visibilityState === 'hidden') {
        reject(new Error('Please keep the page active while getting location'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          console.log('GPS Location obtained:', { lat: latitude, lng: longitude });
          resolve({ 
            lat: latitude, 
            lng: longitude, 
            accuracy: position.coords.accuracy 
          });
        },
        (error) => {
          console.error('GPS Error:', error);
          let errorMessage = 'Unable to get your location';
          
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please enable location permission in your browser settings or enter pincode manually.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable. Please check your GPS settings or use pincode instead.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again or enter pincode manually.';
              break;
            default:
              errorMessage = 'GPS error occurred. Please enter your pincode manually.';
          }
          
          reject(new Error(errorMessage));
        },
        options
      );
    });
  }

  // Reverse geocode coordinates to get address
  static async reverseGeocode(lat, lng) {
    try {
      // In a real app, this would call Google Maps Geocoding API
      // For now, we'll simulate based on coordinates in India
      const mockAddress = this.getMockAddressFromCoordinates(lat, lng);
      return mockAddress;
    } catch (error) {
      throw new Error('Unable to get address from coordinates');
    }
  }

  // Get location details from pincode
  static async getLocationFromPincode(pincode) {
    try {
      // Validate pincode format (6 digits)
      if (!/^\d{6}$/.test(pincode)) {
        throw new Error('Invalid pincode format. Please enter 6 digits.');
      }

      // Mock pincode to location mapping (in real app, use postal API)
      const pincodeData = this.getMockPincodeData(pincode);
      
      if (pincodeData) {
        return pincodeData;
      } else {
        throw new Error('Pincode not found. Please verify and try again.');
      }
    } catch (error) {
      throw error;
    }
  }

  // Mock address data based on coordinates (simulating reverse geocoding)
  static getMockAddressFromCoordinates(lat, lng) {
    // Rough coordinate ranges for major Indian cities
    if (lat >= 17.3 && lat <= 17.5 && lng >= 78.3 && lng <= 78.6) {
      return {
        street: 'Banjara Hills',
        locality: 'Jubilee Hills',
        city: 'Hyderabad',
        state: 'Telangana',
        country: 'India',
        pincode: '500034',
        formattedAddress: 'Banjara Hills, Jubilee Hills, Hyderabad, Telangana 500034'
      };
    } else if (lat >= 17.6 && lat <= 17.8 && lng >= 83.2 && lng <= 83.4) {
      return {
        street: '10-68, PM Palem',
        locality: 'PM Palem',
        city: 'Visakhapatnam',
        state: 'Andhra Pradesh',
        country: 'India',
        pincode: '530041',
        formattedAddress: '10-68, PM Palem, Visakhapatnam, Andhra Pradesh 530041'
      };
    } else if (lat >= 16.4 && lat <= 16.6 && lng >= 80.6 && lng <= 80.8) {
      return {
        street: 'MG Road',
        locality: 'Labbipet',
        city: 'Vijayawada',
        state: 'Andhra Pradesh',
        country: 'India',
        pincode: '520010',
        formattedAddress: 'MG Road, Labbipet, Vijayawada, Andhra Pradesh 520010'
      };
    } else {
      // Default fallback
      return {
        street: 'Main Road',
        locality: 'City Center',
        city: 'Your City',
        state: 'Your State',
        country: 'India',
        pincode: '500001',
        formattedAddress: 'Main Road, City Center, Your City, Your State 500001'
      };
    }
  }

  // Mock pincode database (in real app, integrate with postal API)
  static getMockPincodeData(pincode) {
    const pincodeDatabase = {
      // Hyderabad
      '500001': { city: 'Hyderabad', state: 'Telangana', locality: 'Abids', area: 'Central Hyderabad' },
      '500003': { city: 'Hyderabad', state: 'Telangana', locality: 'Kachiguda', area: 'East Hyderabad' },
      '500004': { city: 'Hyderabad', state: 'Telangana', locality: 'Sultan Bazar', area: 'Central Hyderabad' },
      '500034': { city: 'Hyderabad', state: 'Telangana', locality: 'Banjara Hills', area: 'West Hyderabad' },
      '500081': { city: 'Hyderabad', state: 'Telangana', locality: 'Madhapur', area: 'HITEC City' },
      
      // Visakhapatnam Pincodes - All show GEETH365 doctors
      '530001': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'General Post Office', area: 'Central Vizag', showGEETH365Doctors: true },
      '530002': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Town Kotha Road', area: 'Central Vizag', showGEETH365Doctors: true },
      '530003': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Dwaraka Nagar', area: 'North Vizag', showGEETH365Doctors: true },
      '530004': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Seethammadhara', area: 'North Vizag', showGEETH365Doctors: true },
      '530005': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Lawsons Bay', area: 'Beach Road', showGEETH365Doctors: true },
      '530006': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Maharanipeta', area: 'Central Vizag', showGEETH365Doctors: true },
      '530007': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Jagadamba', area: 'Central Vizag', showGEETH365Doctors: true },
      '530008': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Poorna Market', area: 'Central Vizag', showGEETH365Doctors: true },
      '530009': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Beach Road', area: 'Coastal Area', showGEETH365Doctors: true },
      '530010': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Allipuram', area: 'Central Vizag', showGEETH365Doctors: true },
      '530011': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'One Town', area: 'Central Vizag', showGEETH365Doctors: true },
      '530012': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Two Town', area: 'Central Vizag', showGEETH365Doctors: true },
      '530013': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Gajuwaka', area: 'Industrial Area', showGEETH365Doctors: true },
      '530014': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Kurmannapalem', area: 'Industrial Area', showGEETH365Doctors: true },
      '530015': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Malkapuram', area: 'Residential Area', showGEETH365Doctors: true },
      '530016': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Madhurawada', area: 'IT Corridor', showGEETH365Doctors: true },
      '530017': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'MVP Colony', area: 'Sector 1', showGEETH365Doctors: true },
      '530018': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Siripuram', area: 'Upscale Area', showGEETH365Doctors: true },
      '530019': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Rushikonda', area: 'Beach Area', showGEETH365Doctors: true },
      '530020': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Kailasagiri', area: 'Hill Area', showGEETH365Doctors: true },
      '530021': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Pendurthi', area: 'Outskirts', showGEETH365Doctors: true },
      '530022': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Anakapalle Road', area: 'Highway Area', showGEETH365Doctors: true },
      '530031': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Akkayyapalem', area: 'North Vizag', showGEETH365Doctors: true },
      '530032': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Pedagantyada', area: 'North Vizag', showGEETH365Doctors: true },
      '530040': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'Bheemunipatnam', area: 'Coastal Town', showGEETH365Doctors: true },
      '530041': { city: 'Visakhapatnam', state: 'Andhra Pradesh', locality: 'PM Palem', area: 'PM Palem', showGEETH365Doctors: true },
      
      // Chittor Pincodes - Show GEETH365 doctors for Chittor area
      '517127': { city: 'Chittor', state: 'Andhra Pradesh', locality: 'Apollo Medical College', area: 'Medical District', showGEETH365Doctors: true },
      '517001': { city: 'Chittor', state: 'Andhra Pradesh', locality: 'Chittor Fort', area: 'Heritage Area', showGEETH365Doctors: true },
      '517002': { city: 'Chittor', state: 'Andhra Pradesh', locality: 'Tirupati Road', area: 'Main City', showGEETH365Doctors: true },
      
      // Rajahmundry (Rajamahendravaram) Pincodes - Show GEETH365 doctors for Rajahmundry area
      '533104': { city: 'Rajamahendravaram', state: 'Andhra Pradesh', locality: 'Eye Care Center', area: 'Medical District', showGEETH365Doctors: true },
      '533101': { city: 'Rajamahendravaram', state: 'Andhra Pradesh', locality: 'Gandhi Nagar', area: 'Central Rajahmundry', showGEETH365Doctors: true },
      '533102': { city: 'Rajamahendravaram', state: 'Andhra Pradesh', locality: 'Syamala Nagar', area: 'Residential Area', showGEETH365Doctors: true },
      '533103': { city: 'Rajamahendravaram', state: 'Andhra Pradesh', locality: 'R.A.C.C ICU Critical Care Unit', area: 'Medical District', showGEETH365Doctors: true },
      '533105': { city: 'Rajamahendravaram', state: 'Andhra Pradesh', locality: 'Morampudi', area: 'Outskirts', showGEETH365Doctors: true },
      
      // Guntur Pincodes - Show GEETH365 doctors for Guntur area
      '522002': { city: 'Guntur', state: 'Andhra Pradesh', locality: 'Shri Hospital', area: 'Medical District', showGEETH365Doctors: true },
      '522001': { city: 'Guntur', state: 'Andhra Pradesh', locality: 'Gandhi Nagar', area: 'Central Guntur', showGEETH365Doctors: true },
      '522003': { city: 'Guntur', state: 'Andhra Pradesh', locality: 'Lakshmipuram', area: 'Residential Area', showGEETH365Doctors: true },
      '522004': { city: 'Guntur', state: 'Andhra Pradesh', locality: 'Amaravathi Road', area: 'Highway Area', showGEETH365Doctors: true },
      
      // Tanuku Pincodes - Show GEETH365 doctors for Tanuku area  
      '534211': { city: 'Tanuku', state: 'Andhra Pradesh', locality: 'Kona Hospital', area: 'Medical District', showGEETH365Doctors: true },
      '534201': { city: 'Tanuku', state: 'Andhra Pradesh', locality: 'Main Road', area: 'Central Tanuku', showGEETH365Doctors: true },
      '534202': { city: 'Tanuku', state: 'Andhra Pradesh', locality: 'Railway Station', area: 'Transport Hub', showGEETH365Doctors: true },
      
      // Vijayawada Pincodes - Show GEETH365 doctors for Vijayawada area
      '520008': { city: 'Vijayawada', state: 'Andhra Pradesh', locality: 'Ramesh Hospital', area: 'Medical District', showGEETH365Doctors: true },
      '520001': { city: 'Vijayawada', state: 'Andhra Pradesh', locality: 'One Town', area: 'Central Vijayawada', showGEETH365Doctors: true },
      '520003': { city: 'Vijayawada', state: 'Andhra Pradesh', locality: 'Gandhi Nagar', area: 'East Vijayawada', showGEETH365Doctors: true },
      '520010': { city: 'Vijayawada', state: 'Andhra Pradesh', locality: 'Labbipet', area: 'Commercial Area', showGEETH365Doctors: true },
      '520004': { city: 'Vijayawada', state: 'Andhra Pradesh', locality: 'Benz Circle', area: 'Central Hub', showGEETH365Doctors: true },
      
      // Nellore Pincodes - Show GEETH365 doctors for Nellore area
      '524003': { city: 'Nellore', state: 'Andhra Pradesh', locality: 'Narayana Medical College', area: 'Medical District', showGEETH365Doctors: true },
      '524001': { city: 'Nellore', state: 'Andhra Pradesh', locality: 'Clock Tower', area: 'Central Nellore', showGEETH365Doctors: true },
      '524002': { city: 'Nellore', state: 'Andhra Pradesh', locality: 'Stonehouse Pet', area: 'Commercial Area', showGEETH365Doctors: true },
      '524004': { city: 'Nellore', state: 'Andhra Pradesh', locality: 'Vedayapalem', area: 'Residential Area', showGEETH365Doctors: true },
      
      // Hyderabad Pincodes - Show GEETH365 doctors for Hyderabad area
      '500085': { city: 'Hyderabad', state: 'Telangana', locality: 'Kukatpally', area: 'HITEC City Area', showGEETH365Doctors: true },
      
      // Bhimavaram Pincodes - Show GEETH365 doctors for Bhimavaram area
      '534202': { city: 'Bhimavaram', state: 'Andhra Pradesh', locality: 'Surya Hospital', area: 'Medical District', showGEETH365Doctors: true },
      '534201': { city: 'Bhimavaram', state: 'Andhra Pradesh', locality: 'Main Road', area: 'Central Bhimavaram', showGEETH365Doctors: true },
      '534203': { city: 'Bhimavaram', state: 'Andhra Pradesh', locality: 'Railway Station', area: 'Transport Hub', showGEETH365Doctors: true },
      
      // Ganapavaram Pincodes - Show GEETH365 doctors for Ganapavaram area
      '534198': { city: 'Ganapavaram', state: 'Andhra Pradesh', locality: 'Sunrise Hospitals', area: 'Medical District', showGEETH365Doctors: true },
      '534197': { city: 'Ganapavaram', state: 'Andhra Pradesh', locality: 'Main Road', area: 'Central Ganapavaram', showGEETH365Doctors: true },
      '534199': { city: 'Ganapavaram', state: 'Andhra Pradesh', locality: 'Bus Stand', area: 'Transport Hub', showGEETH365Doctors: true },
      '520001': { city: 'Vijayawada', state: 'Andhra Pradesh', locality: 'One Town', area: 'Central Vijayawada' },
      '520003': { city: 'Vijayawada', state: 'Andhra Pradesh', locality: 'Gandhi Nagar', area: 'East Vijayawada' },
      '520010': { city: 'Vijayawada', state: 'Andhra Pradesh', locality: 'Labbipet', area: 'Commercial Area' },
      
      // Bangalore
      '560001': { city: 'Bangalore', state: 'Karnataka', locality: 'Chickpet', area: 'Central Bangalore' },
      '560025': { city: 'Bangalore', state: 'Karnataka', locality: 'Domlur', area: 'East Bangalore' },
      '560066': { city: 'Bangalore', state: 'Karnataka', locality: 'Whitefield', area: 'IT Corridor' },
      
      // Chennai
      '600001': { city: 'Chennai', state: 'Tamil Nadu', locality: 'Fort St. George', area: 'Central Chennai' },
      '600028': { city: 'Chennai', state: 'Tamil Nadu', locality: 'T. Nagar', area: 'Commercial Area' },
      '600096': { city: 'Chennai', state: 'Tamil Nadu', locality: 'Velachery', area: 'IT Corridor' },
      
      // Mumbai
      '400001': { city: 'Mumbai', state: 'Maharashtra', locality: 'Fort', area: 'South Mumbai' },
      '400051': { city: 'Mumbai', state: 'Maharashtra', locality: 'Bandra West', area: 'Suburban Mumbai' },
      '400093': { city: 'Mumbai', state: 'Maharashtra', locality: 'Powai', area: 'IT Hub' },
      
      // Delhi
      '110001': { city: 'New Delhi', state: 'Delhi', locality: 'Connaught Place', area: 'Central Delhi' },
      '110016': { city: 'New Delhi', state: 'Delhi', locality: 'Lajpat Nagar', area: 'South Delhi' },
      '110092': { city: 'New Delhi', state: 'Delhi', locality: 'Sector 14', area: 'Dwarka' }
    };

    const data = pincodeDatabase[pincode];
    if (data) {
      return {
        ...data,
        country: 'India',
        pincode: pincode,
        formattedAddress: `${data.locality}, ${data.area}, ${data.city}, ${data.state} ${pincode}`
      };
    }
    return null;
  }

  // Validate Indian pincode format
  static isValidPincode(pincode) {
    return /^\d{6}$/.test(pincode);
  }

  // Get nearby cities based on pincode (for delivery coverage)
  static getNearbyDeliveryAreas(pincode) {
    const firstTwoDigits = pincode.substring(0, 2);
    
    // Postal circle mapping for delivery coverage
    const postalCircles = {
      '50': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar'], // Telangana
      '51': ['Vijayawada', 'Visakhapatnam', 'Tirupati', 'Guntur'], // Andhra Pradesh
      '56': ['Bangalore', 'Mysore', 'Hubli', 'Mangalore'], // Karnataka
      '60': ['Chennai', 'Coimbatore', 'Madurai', 'Salem'], // Tamil Nadu
      '40': ['Mumbai', 'Pune', 'Nagpur', 'Nashik'], // Maharashtra
      '11': ['Delhi', 'Gurgaon', 'Noida', 'Faridabad'] // Delhi NCR
    };

    return postalCircles[firstTwoDigits] || ['Your Area'];
  }

  // Calculate distance between two coordinates (for doctor proximity)
  static calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    return Math.round(distance * 10) / 10; // Round to 1 decimal place
  }

  static toRad(degrees) {
    return degrees * (Math.PI / 180);
  }
}

export default LocationService;